/*
 * "pdp-redesign" product template
 * - <pdp-purchase-options>: subscribe & save / one-time purchase selector
 * - <pdp-buy-buttons>: keeps the price shown in the add to cart button in sync
 * - <pdp-gallery-nav>: prev/next arrows on the main product image
 */
(function () {
  const PRICE_CHANGE_EVENT = 'pdp:price-change';

  const parseJSON = (element) => {
    if (!element) return null;
    try {
      return JSON.parse(element.textContent);
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const decodeMoneyFormat = (format) => {
    if (!format) return '${{amount}}';
    // money_format can contain HTML/entities (e.g. "<span class=money>&euro;{{amount}}</span>")
    const text = new DOMParser().parseFromString(format, 'text/html').documentElement.textContent;
    return text || '${{amount}}';
  };

  const formatMoney = (cents, format) => {
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const formatWithDelimiters = (number, precision, thousands, decimal) => {
      if (Number.isNaN(number) || number == null) return '0';
      const parts = (number / 100).toFixed(precision).split('.');
      const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      return parts[1] ? `${whole}${decimal}${parts[1]}` : whole;
    };

    const match = format.match(placeholder);
    let value;
    switch (match ? match[1] : 'amount') {
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'", '.');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ', ',');
        break;
      case 'amount_with_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_with_period_and_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', '.');
        break;
      default:
        value = formatWithDelimiters(cents, 2, ',', '.');
    }
    return format.replace(placeholder, value);
  };

  // Dawn's pub/sub (pubsub.js + constants.js are loaded in the layout)
  const subscribeTo = (eventKey, callback) => {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return null;
    return subscribe(PUB_SUB_EVENTS[eventKey], callback);
  };

  if (!customElements.get('pdp-purchase-options')) {
    customElements.define(
      'pdp-purchase-options',
      class PdpPurchaseOptions extends HTMLElement {
        connectedCallback() {
          this.sectionId = this.dataset.section;
          this.variantId = this.dataset.variantId;
          this.data = parseJSON(this.querySelector('[data-purchase-options-data]')) || { variants: {} };
          this.planInput = this.querySelector('[data-selling-plan-input]');
          this.cards = Array.from(this.querySelectorAll('[data-option-card]'));

          this.addEventListener('change', this.onChange.bind(this));
          this.addEventListener('click', this.onClick.bind(this));

          this.unsubscribeVariantChange = subscribeTo('variantChange', (event) => {
            const data = event && event.data;
            if (!data || data.sectionId !== this.sectionId || !data.variant) return;
            this.variantId = String(data.variant.id);
            this.update();
          });

          this.update();
        }

        disconnectedCallback() {
          if (this.unsubscribeVariantChange) this.unsubscribeVariantChange();
        }

        onChange(event) {
          const select = event.target.closest('[data-plan-select]');
          if (select) {
            const card = select.closest('[data-option-card]');
            card.dataset.planId = select.value;
            // Picking a delivery frequency implies the subscription option
            this.check(card);
          }
          this.update();
        }

        onClick(event) {
          const card = event.target.closest('[data-option-card]');
          if (!card || event.target.closest('label, select, a')) return;
          if (this.check(card)) this.update();
        }

        check(card) {
          const radio = card.querySelector('.pdp-option__radio');
          if (!radio || radio.checked) return false;
          radio.checked = true;
          return true;
        }

        getSelectedCard() {
          return this.cards.find((card) => {
            const radio = card.querySelector('.pdp-option__radio');
            return radio && radio.checked;
          });
        }

        update() {
          const variant = this.data.variants[this.variantId];
          const selectedCard = this.getSelectedCard();

          this.cards.forEach((card) => {
            card.classList.toggle('is-selected', card === selectedCard);
            if (variant) this.renderCard(card, variant);
          });

          const sellingPlanId = selectedCard && selectedCard.dataset.groupId ? selectedCard.dataset.planId : '';
          if (this.planInput) {
            this.planInput.value = sellingPlanId;
            this.planInput.disabled = !sellingPlanId;
          }

          if (!variant) return;
          const plan = sellingPlanId ? variant.plans[sellingPlanId] : null;
          this.currentPrice = plan ? plan.price : variant.price;

          this.dispatchEvent(
            new CustomEvent(PRICE_CHANGE_EVENT, {
              bubbles: true,
              detail: {
                sectionId: this.sectionId,
                variantId: this.variantId,
                sellingPlanId,
                price: this.currentPrice,
              },
            })
          );
        }

        renderCard(card, variant) {
          const plan = card.dataset.groupId ? variant.plans[card.dataset.planId] : null;
          const source = plan || variant;
          const hasCompare = source.compare_at_price > source.price;

          const priceElement = card.querySelector('[data-price]');
          if (priceElement) priceElement.innerHTML = source.price_html;

          const compareElement = card.querySelector('[data-compare-price]');
          if (compareElement) {
            compareElement.hidden = !hasCompare;
            compareElement.innerHTML = hasCompare ? source.compare_at_price_html : '';
          }

          if (!card.dataset.groupId) return;
          const savings =
            plan && hasCompare ? Math.round(((source.compare_at_price - source.price) * 100) / source.compare_at_price) : 0;
          card.querySelectorAll('[data-savings]').forEach((element) => {
            element.textContent = savings;
          });
          const badge = card.querySelector('[data-savings-badge][data-hide-when-zero]');
          if (badge) badge.hidden = savings === 0;
        }
      }
    );
  }

  // Prev/next arrows on the main product image: step through the visible thumbnails,
  // so Dawn's media-gallery keeps handling the active media, thumbnail state and variants.
  if (!customElements.get('pdp-gallery-nav')) {
    customElements.define(
      'pdp-gallery-nav',
      class PdpGalleryNav extends HTMLElement {
        connectedCallback() {
          this.gallery = this.closest('media-gallery');
          this.querySelectorAll('[data-direction]').forEach((button) => {
            button.addEventListener('click', () => this.go(parseInt(button.dataset.direction, 10)));
          });
        }

        go(step) {
          if (!this.gallery) return;
          const thumbnails = Array.from(this.gallery.querySelectorAll('.thumbnail-list__item')).filter(
            (item) => item.offsetParent !== null
          );
          if (thumbnails.length < 2) return;
          const current = thumbnails.findIndex((item) => item.querySelector('[aria-current]'));
          const next = (Math.max(current, 0) + step + thumbnails.length) % thumbnails.length;
          const button = thumbnails[next].querySelector('button');
          if (button) button.click();
        }
      }
    );
  }

  if (!customElements.get('pdp-buy-buttons')) {
    customElements.define(
      'pdp-buy-buttons',
      class PdpBuyButtons extends HTMLElement {
        connectedCallback() {
          this.sectionId = this.dataset.section;
          this.data = parseJSON(this.querySelector('[data-buy-buttons-data]')) || { variants: {} };
          this.moneyFormat = decodeMoneyFormat(this.data.moneyFormat);
          this.priceElement = this.querySelector('[data-atc-price]');
          this.quantityInput = this.querySelector('.quantity__input');
          this.unitPrice = parseInt(this.dataset.unitPrice, 10);

          this.onPriceChange = (event) => {
            if (!event.detail || event.detail.sectionId !== this.sectionId || event.detail.price == null) return;
            this.unitPrice = event.detail.price;
            this.render();
          };
          document.addEventListener(PRICE_CHANGE_EVENT, this.onPriceChange);

          if (this.quantityInput) {
            this.onQuantityChange = () => this.render();
            this.quantityInput.addEventListener('change', this.onQuantityChange);
            this.quantityInput.addEventListener('input', this.onQuantityChange);
          }

          this.unsubscribers = [
            subscribeTo('variantChange', (event) => {
              const data = event && event.data;
              if (!data || data.sectionId !== this.sectionId || !data.variant) return;
              // The purchase options block reports its own (subscription) price
              if (this.getPurchaseOptions()) return;
              const price = this.data.variants[String(data.variant.id)];
              if (price == null) return;
              this.unitPrice = price;
              this.render();
            }),
            subscribeTo('quantityUpdate', () => this.render()),
          ].filter(Boolean);

          const purchaseOptions = this.getPurchaseOptions();
          if (purchaseOptions && purchaseOptions.currentPrice != null) this.unitPrice = purchaseOptions.currentPrice;
          this.render();
        }

        disconnectedCallback() {
          document.removeEventListener(PRICE_CHANGE_EVENT, this.onPriceChange);
          if (this.quantityInput && this.onQuantityChange) {
            this.quantityInput.removeEventListener('change', this.onQuantityChange);
            this.quantityInput.removeEventListener('input', this.onQuantityChange);
          }
          (this.unsubscribers || []).forEach((unsubscribe) => unsubscribe());
        }

        getPurchaseOptions() {
          return document.querySelector(`pdp-purchase-options[data-section="${this.sectionId}"]`);
        }

        render() {
          if (!this.priceElement || Number.isNaN(this.unitPrice)) return;
          const quantity = this.quantityInput ? Math.max(parseInt(this.quantityInput.value, 10) || 1, 1) : 1;
          this.priceElement.textContent = formatMoney(this.unitPrice * quantity, this.moneyFormat);
        }
      }
    );
  }
})();
